import React, { useEffect } from 'react'
import { HandleButton } from './handle-button.tsx'
import type { MagneticHandlePoint } from '../model/model'
import { useMagneticHandleNodeState, useMagneticHandlesSelector } from '../model/magnetic-handles-provider'
import type { InternalMagneticHandleTriggerZoneHoverState } from './handle-trigger-zone.tsx'

type MagneticHandleOverlayProps = {
    connectionRuntimeActive?: boolean
    leftDefaultPoint: MagneticHandlePoint
    hoverState: InternalMagneticHandleTriggerZoneHoverState
    isConnectingFromNode?: boolean
    nodeId: string
    nodeRef?: React.RefObject<HTMLDivElement | null>
    rightDefaultPoint: MagneticHandlePoint
    selected?: boolean
    showLeftPlus?: boolean
    showRightPlus?: boolean
    forceHide?: boolean
}

type SideHandleState = {
    followPointer: boolean
    magnetized: boolean
    point: { x: number; y: number }
}

function resolveSideHandleState({
    defaultPoint,
    hoverState,
    magnetPoint,
    magnetSide,
    side,
}: {
    defaultPoint: { x: number; y: number }
    hoverState: InternalMagneticHandleTriggerZoneHoverState
    magnetPoint: { x: number; y: number }
    magnetSide: 'left' | 'right' | null
    side: 'left' | 'right'
}): SideHandleState {
    const activeHoverState = hoverState?.side === side ? hoverState : null
    const isMagnetized = magnetSide === side || (magnetSide === null && activeHoverState?.magnetized)

    return {
        followPointer: activeHoverState !== null,
        magnetized: isMagnetized!,
        point: magnetSide === side ? magnetPoint : activeHoverState?.point ?? defaultPoint,
    }
}

export const HandleOverlay = React.memo(function HandleOverlay({
    leftDefaultPoint,
    hoverState,
    isConnectingFromNode = false,
    nodeId,
    nodeRef,
    rightDefaultPoint,
    selected = false,
    showLeftPlus = true,
    showRightPlus = true,
    forceHide = false,
}: MagneticHandleOverlayProps) {
    const canConnectDropTarget = useMagneticHandlesSelector((state) => state.canConnectDropTarget)
    const {
        dropTarget,
        magnetPoint,
        magnetSide,
    } = useMagneticHandleNodeState(nodeId)
    const visible = !forceHide && !isConnectingFromNode && (selected || hoverState !== null || magnetSide !== null || dropTarget)
    const leftHandleState = resolveSideHandleState({
        defaultPoint: leftDefaultPoint,
        hoverState,
        magnetPoint,
        magnetSide,
        side: 'left',
    })
    const rightHandleState = resolveSideHandleState({
        defaultPoint: rightDefaultPoint,
        hoverState,
        magnetPoint,
        magnetSide,
        side: 'right',
    })
    const dropTargetNotConnectable = dropTarget && !canConnectDropTarget
    const activeTilt = dropTarget && !dropTargetNotConnectable

    useEffect(() => {
        if (!activeTilt || !nodeRef?.current) return

        const el = nodeRef.current
        let rafId: number

        const handleMouseMove = (e: MouseEvent) => {
            if (rafId) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
                const rect = el.getBoundingClientRect()
                const x = e.clientX - rect.left
                const y = e.clientY - rect.top
                
                const clampedX = Math.max(0, Math.min(x, rect.width))
                const clampedY = Math.max(0, Math.min(y, rect.height))
                
                const xPct = (clampedX / rect.width) * 2 - 1
                const yPct = (clampedY / rect.height) * 2 - 1
                
                const maxTilt = 8
                const rotateX = yPct * -maxTilt
                const rotateY = xPct * maxTilt

                el.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`
                el.style.transition = 'transform 0.05s linear'
            })
        }

        const handleMouseLeave = () => {
            if (rafId) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
                el.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`
                el.style.transition = 'transform 0.3s ease-out'
                setTimeout(() => {
                    if (el.style.transform.includes('rotateX(0deg)')) {
                        el.style.transform = ''
                        el.style.transition = ''
                    }
                }, 300)
            })
        }

        el.addEventListener('mousemove', handleMouseMove)
        el.addEventListener('mouseleave', handleMouseLeave)

        return () => {
            if (rafId) cancelAnimationFrame(rafId)
            el.removeEventListener('mousemove', handleMouseMove)
            el.removeEventListener('mouseleave', handleMouseLeave)
            
            el.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`
            el.style.transition = 'transform 0.3s ease-out'
            setTimeout(() => {
                if (el.style.transform.includes('rotateX(0deg)')) {
                    el.style.transform = ''
                    el.style.transition = ''
                }
            }, 300)
        }
    }, [activeTilt, nodeRef])

    return (
        <>
            {dropTarget && !dropTargetNotConnectable && (
                <div
                    className="pointer-events-none absolute z-[110] inset-0 rounded-2xl transition-opacity duration-160"
                    style={{
                        padding: '2px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                    }}
                >
                    <div
                        className="absolute inset-[-100%] animate-[spin_2s_linear_infinite]"
                        style={{
                            background: 'conic-gradient(from 0deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,1) 100%)'
                        }}
                    />
                </div>
            )}
            {dropTargetNotConnectable ? (
                <div className="pointer-events-none absolute inset-0 z-[110] flex items-center justify-center rounded-2xl bg-black/48 backdrop-blur-sm">
                    <span className="rounded-full border border-white/18 bg-[rgba(10,10,12,0.3)] px-4 py-1 text-[14px] font-medium leading-none text-[#E1E1E1]">无法连接</span>
                </div>
            ) : null}
            <HandleButton
                followPointer={leftHandleState.followPointer}
                side="left"
                visible={visible}
                x={leftHandleState.point.x}
                y={leftHandleState.point.y}
                showPlus={showLeftPlus}
            />
            <HandleButton
                followPointer={rightHandleState.followPointer}
                side="right"
                visible={visible}
                x={rightHandleState.point.x}
                y={rightHandleState.point.y}
                showPlus={showRightPlus}
            />
        </>
    )
})